import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { AccordionSection, Button } from '@aimsos/ui';

import { integrationLabelKey } from '../constants/integrations.js';
import { useEntities } from '../hooks/useEntities.js';
import EntityPickerCard from './EntityPickerCard.jsx';
import EntityPickerModal from './EntityPickerModal.jsx';
import InlineNote from './InlineNote.jsx';
import PickerOptionGroup from './PickerOptionGroup.jsx';
import { PickerError, PickerSkeleton } from './PickerStates.jsx';

/**
 * `CHOOSE ENTITY` (CT-5191).
 *
 * The inline list shows only the **featured** entities — four, as a curated
 * shortlist rather than a slice of the catalogue — with `Browse all entities`
 * opening the full one (CT-5192). The integration pills that used to sit here
 * are gone with the redesign; source filtering now lives in the modal, which is
 * where the whole catalogue is.
 */
function EntityPicker({ value, onSelect }) {
  const { t } = useTranslation('dashboards');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { entities, isLoading, error, refetch } = useEntities();

  /**
   * The featured shortlist, plus whatever is selected.
   *
   * Without the second half, choosing a non-featured entity in the picker modal
   * left the Data tab showing four rows with none of them checked — the user
   * could not see their own choice anywhere on the tab.
   */
  const visibleEntities = useMemo(
    () => entities.filter((entity) => entity.featured || entity.id === value),
    [entities, value],
  );

  const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const labels = useMemo(
    () => ({
      ungoverned: t('widgetBuilder.data.entities.ungoverned'),
      pii: t('widgetBuilder.data.entities.pii'),
    }),
    [t],
  );

  // Both routes resolve the entity from the same catalogue, so the composite
  // uuid — which the widget is stored against, and which only the catalogue
  // knows — travels with every selection however it was made.
  //
  // Cards are keyed by the composite (`id`), which is unique; the form still
  // receives the primary member as its `entityRef`, the uuid the column
  // requests take.
  const commitSelection = useCallback(
    (compositeId) => {
      const entity = entities.find((candidate) => candidate.id === compositeId);
      if (!entity) return;
      onSelect(
        entity.primaryEntityUuid,
        entity.name,
        entity.integration ?? '',
        entity.compositeEntityUuid,
      );
    },
    [entities, onSelect],
  );

  const handleCommit = useCallback(
    (entityRef) => {
      setIsModalOpen(false);
      // The same setter the inline cards call, so the downstream-reset warning
      // fires identically whichever route the user took.
      commitSelection(entityRef);
    },
    [commitSelection],
  );

  const handleInlineSelect = commitSelection;

  const body = () => {
    if (isLoading) return <PickerSkeleton count={4} height={72} />;
    if (error) {
      return (
        <PickerError
          title={t('widgetBuilder.data.entities.errorTitle')}
          subtitle={t('widgetBuilder.data.entities.errorSubtitle')}
          retryLabel={t('widgetBuilder.data.retry')}
          onRetry={refetch}
        />
      );
    }

    return (
      <PickerOptionGroup
        ariaLabel={t('widgetBuilder.data.entities.title')}
        columns={2}
        className="grid gap-3 sm:grid-cols-2"
      >
        {visibleEntities.map((entity) => (
          <EntityPickerCard
            key={entity.id}
            entity={entity}
            integrationLabel={t(integrationLabelKey(entity.integration) ?? '') || entity.integration}
            metaLabel={t('widgetBuilder.data.entities.memberCount', { count: entity.memberCount })}
            ungovernedLabel={labels.ungoverned}
            piiLabel={labels.pii}
            selected={value === entity.id}
            onSelect={handleInlineSelect}
          />
        ))}
      </PickerOptionGroup>
    );
  };

  return (
    <>
      <AccordionSection title={t('widgetBuilder.data.entities.title')} titleSize="xs" defaultOpen>
        <div className="flex flex-col gap-3 pb-4 pt-3">
          <div className="flex justify-end">
            <Button variant="tertiary" size="sm" onClick={handleOpenModal}>
              {t('widgetBuilder.data.entities.browseAll')}
            </Button>
          </div>

          {body()}

          <InlineNote>{t('widgetBuilder.data.entities.moreAvailable')}</InlineNote>
        </div>
      </AccordionSection>

      <EntityPickerModal
        isOpen={isModalOpen}
        committedEntityRef={value}
        onClose={handleCloseModal}
        onCommit={handleCommit}
      />
    </>
  );
}

EntityPicker.propTypes = {
  /** The selected composite's uuid — what tells one card from another. */
  value: PropTypes.string,
  /** `(entityRef, label, integration, compositeEntityUuid) => void` — `entityRef` is the composite's primary member. */
  onSelect: PropTypes.func.isRequired,
};

export default EntityPicker;
